import React, { useEffect, useState, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Project } from '@models/project.model';
import { useHistory, useParams } from 'react-router-dom';
import { SecondarySidebar } from '@components/organisms/SecondarySidebar';
import { EmptyData } from '@components/molecules/EmptyData';
import { Loading } from '@components/atoms/Loading';
import { useDocuments, useTask } from '@hooks/api';
import { CreateEditProjectModal } from './components/CreateEditProjectModal/index';
import { useProject } from '@hooks/api/projects/useProject';
import { AssistantProjectButton } from '@components/molecules/AssistantProjectButton';
import { useRememberedSelection } from '@hooks/useRememberedSelection';
import { ProjectCard } from './components/ProjectCard';
import { BudgetCard } from './components/BudgetCard';
import { TimelineCard } from './components/TimelineCard';
import { useGetMaterialAndEquipment } from '@hooks/api/material&Equipment/useGetMaterial&Equipment';
import { useGetProjectBudgetSummary } from '@hooks/api/lineItem/useGetProjectBudgetSummary';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Tab } from '@components/atoms/Tab';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import { canAccess } from '@utils/featureFlag';
import { UserRoles } from '@models/user.model';
import {
  roleRank,
  ROLE_RANK,
  TEMPLATE_ACTIONS_MIN_RANK,
  isExternalViewerRole,
} from '@constants/roleHierarchy';
import { ProjectFeed } from './components/ProjectFeed';
import { useProjectFeedByProjects } from './components/ProjectFeed/hooks';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useDeleteProject } from '@hooks/api/projects/useDeleteProject';
import { useToggleProjectFavorite } from '@hooks/api/projects/useToggleProjectFavorite';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { BudgetTableModal } from './components/BudgetTableModal';
import { BillOfMaterialsModal } from './components/BillOfMaterialsModal';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import ProjectGeofenceModal from '@components/organisms/ProjectGeofenceModal';
import UsersInsideAreaList from '@components/organisms/UsersInsideAreaList';
import useUsersInsideArea from '@hooks/api/usersInsideArea/useUsersInsideArea';
import { UsersInsideAreaPresence } from '@models/usersInsideArea.model';
import { DocumentsTable } from '@components/organisms/Documents';
import { ProjectEvidenceTab } from './components/ProjectEvidenceTab';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { ProjectInvoicesTab } from '@components/organisms/ProjectInvoicesTab';
import { useGetInvoicesByProject } from '@hooks/api/invoice';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { useProjectsByState } from '@hooks/api/projects/useProjectsByState';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import {
  ProjectStatus,
  getDisplayProjectStatus,
} from '@models/projectStatus.model';
import { getProjectStateColor } from '@utils/getProjectStateColor';
import { SingleValue, MultiValue, ActionMeta } from 'react-select';
import { ImportTaskTemplateModal } from '@components/organisms/ImportTaskTemplateModal';
import { ReportIncidentModal } from '@components/organisms/ReportIncidentModal';
import { useTranslation } from '@hooks/useTranslation';
import { InProgressAiPlans } from '@components/molecules/InProgressAiPlans';

interface RouteParams {
  id: string;
}

const ProjectsPage: React.FC = () => {
  const [countFeed, setCountFeed] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const { role, isReadOnly } = useAppSelector((state: RootState) => state.user);
  const tProjects = useTranslation('projects');
  const tCommon = useTranslation('common');
  // Sprint 25, story-127. REVIEWER and CLIENT are both read-only external viewers and see a
  // reduced project view (no budget/timeline cards, no Visitors tab, no edit/delete/acknowledge)
  // — every write here now also fails server-side (story-126), so this is a UI-level mirror of
  // that server rule, not the only thing enforcing it.
  const isReviewer = role === UserRoles.REVIEWER;
  const isExternalViewer = isExternalViewerRole(role);
  const readOnly = isReadOnly || isExternalViewer;
  const history = useHistory();
  useState(false);
  const [isCreateEditProjectModalOpen, setIsCreateEditProjectModalOpen] =
    useState(false);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] =
    useState(false);
  const [isTaskDetailModal, setIsTaskDetailModal] = useState(false);
  const [inventory, setInventory] = useState<any[]>([]);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isBillOfMaterialsModalOpen, setIsBillOfMaterialsModalOpen] =
    useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [activeTab, setActiveTab] = useState('projectFeed');
  const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
  const [isImportTemplateModalOpen, setIsImportTemplateModalOpen] =
    useState(false);
  const [isReportIncidentModalOpen, setIsReportIncidentModalOpen] =
    useState(false);
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = useState(false);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | null>(
    null,
  );
  const { id } = useParams<RouteParams>();

  // Use the new hook with state filtering
  const selectedStates = selectedState?.value
    ? [selectedState.value as string]
    : undefined;
  const { data: projects = [], isLoading: projectsLoading } =
    useProjectsByState(selectedStates);
  const { fetchTask, task } = useTask();
  const { data, refetch: refetchMaterialsAndEquipments } =
    useGetMaterialAndEquipment(id);
  const { data: project } = useProject(id);
  const { data: budgetSummary } = useGetProjectBudgetSummary(id);
  const { mutate: deleteProjectMutation } = useDeleteProject();
  const { mutate: toggleFavorite } = useToggleProjectFavorite();

  const { data: documentsData } = useDocuments({
    projectIds: id ? [id] : undefined,
  });

  // Live count for the Invoices tab badge. Shares the React Query cache with the
  // ProjectInvoicesTab organism (same query key), so this doesn't double-fetch.
  const { data: projectInvoices = [] } = useGetInvoicesByProject(
    canAccess('INVOICES', role) ? id : null,
  );

  // Live count of users currently inside any geofence belonging to this
  // project. Polls every 30s (built into the hook) so the badge stays fresh
  // without us having to invalidate manually.
  const { data: visitorsNow = [] } = useUsersInsideArea(
    id ? { projectId: id, presence: UsersInsideAreaPresence.INSIDE } : null,
  );
  const visitorsNowCount = visitorsNow.length;

  // Use ProjectFeed hook for project activities - memoize projectsId to prevent unnecessary resets
  const projectsId = useMemo(() => (id ? [id] : []), [id]);
  const { activities, paginationActions, totalCount } =
    useProjectFeedByProjects({
      projectsId,
    });

  const {
    selectedItem,
    onSelect: onRememberedSelect,
    clearStored: clearRememberedSelection,
  } = useRememberedSelection({
    key: 'project',
    items: projects || [],
    getId: (p) => p.projectId,
    basePath: '/projects',
  });

  useEffect(() => {
    refetchMaterialsAndEquipments();
    const materialsAndEquipments = [
      ...(data?.materials || []),
      ...(data?.equipments || []),
    ];

    const inventory = materialsAndEquipments.map((item: any) => ({
      ...item,
    }));

    setInventory(inventory);

    const totalCost = materialsAndEquipments.reduce(
      (sum: number, item: any) => {
        return sum + (item.totalCost || 0);
      },
      0,
    );

    setTotalCost(totalCost);
  }, [data, id, refetchMaterialsAndEquipments]);

  useEffect(() => {
    if (id) {
      onRememberedSelect(id);
    }
  }, [id, onRememberedSelect]);

  const deleteProject = async () => {
    deleteProjectMutation(id);
    clearRememberedSelection();
    history.push('/projects');
  };

  const handleEditProject = () => {
    setIsEdit(true);
    toggleCreateEditProjectModal();
  };

  const toggleCreateEditProjectModal = () => {
    setIsCreateEditProjectModalOpen(!isCreateEditProjectModalOpen);
    // Reset isEdit when closing the modal
    if (isCreateEditProjectModalOpen) {
      setIsEdit(false);
    }
  };

  // Sprint 31, story-160: project *creation* moved to the full-page wizard;
  // `CreateEditProjectModal` is edit-only from here on.
  const handleOpenCreateWizard = () => {
    history.push('/projects/new');
  };

  const goToClient = (clientId: string) => {
    history.push(`/clients/${clientId}`);
  };

  const goToSubcontractor = (subcontractorId: string) => {
    history.push(`/subcontractors/${subcontractorId}`);
  };

  const handleOpenTaskDetail = (taskId: string) => {
    fetchTask(taskId);
    setIsTaskDetailModal(true);
  };

  const handleProjectSuccess = (project: Project) => {
    // Navigate to the newly created project
    // onRememberedSelect handles both state update and navigation internally
    if (project.projectId) {
      onRememberedSelect(project.projectId);
    }
  };

  const handleImportTemplateSuccess = () => {
    setIsImportTemplateModalOpen(false);
  };

  // State filter options
  const stateOptions: SelectOption[] = [
    { value: '', label: tProjects.allStates },
    { value: ProjectStatus.ONGOING, label: tProjects.statusOngoing },
    { value: ProjectStatus.BEHIND, label: tProjects.statusBehind },
    { value: ProjectStatus.COMPLETED, label: tProjects.statusCompleted },
    { value: ProjectStatus.ABANDONED, label: tProjects.statusAbandoned },
    { value: ProjectStatus.DELAYED, label: tProjects.statusDelayed },
    { value: ProjectStatus.BIDDING, label: tProjects.statusBidding },
    { value: ProjectStatus.DRAFT, label: tProjects.statusDraft },
  ];

  // Handle state filter change
  const handleStateChange = (
    value: SingleValue<SelectOption> | MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>,
  ) => {
    const selected = value as SelectOption | null;
    // If "All States" is selected or null, clear the filter
    if (!selected || selected.value === '') {
      setSelectedState(null);
    } else {
      setSelectedState(selected);
    }
    // Clear the selected project so the right pane doesn't keep showing a
    // project that isn't in the new filtered list. The useEffect below will
    // auto-pick the first project of the new list once it loads.
    clearRememberedSelection();
    history.push('/projects');
  };

  // Handle project selection when filter changes
  // Only redirect if there's no id in URL (initial load) or if filter explicitly changes
  useEffect(() => {
    // Skip if we have an id in URL - user navigated there intentionally
    // The project data will load via useProject(id)
    if (id) return;

    if (!projectsLoading && projects.length > 0) {
      // No id in URL, select the first project
      const firstProject = projects[0];
      if (firstProject?.projectId) {
        onRememberedSelect(firstProject.projectId);
      }
    }
  }, [projects, projectsLoading, id, onRememberedSelect]);

  // Sprint 25, story-129. One row per job on the sub org's project list: mirror projects
  // (originalProjectId set — legacy same-org self-mirrors are already excluded server-side,
  // ProjectService.excludeSelfMirrors) are grouped under a "Delegated to you (n)" section below
  // "Your projects", instead of sitting unlabelled among the org's own rows. An org with no
  // mirrors sees the flat, unsectioned list unchanged (per the UX doc: "An org with no mirrors
  // shows no 'Delegated to you' header at all").
  const sidebarItems = useMemo(() => {
    const toItem = (project: Project) => ({
      id: project.projectId,
      name: project.name,
      originalProject: project.originalProject?.organization?.name || '',
      iconRight: (
        <div
          className={`w-2 h-2 ${getProjectStateColor(
            getDisplayProjectStatus(project.state, project.isBehind),
          )} rounded-full`}
        />
      ),
    });

    const delegated = projects.filter((p: Project) => !!p.originalProjectId);
    if (delegated.length === 0) {
      return projects.map(toItem);
    }
    const yours = projects.filter((p: Project) => !p.originalProjectId);

    return [
      {
        id: '__section_yours',
        isSectionHeader: true,
        name: tProjects?.sectionYours || 'Your projects',
      },
      ...yours.map(toItem),
      {
        id: '__section_delegated',
        isSectionHeader: true,
        name: (tProjects?.sectionDelegated || 'Delegated to you ({n})').replace(
          '{n}',
          String(delegated.length),
        ),
      },
      ...delegated.map(toItem),
    ];
  }, [projects, tProjects]);

  return (
    <>
      <SecondarySidebar
        selectedItem={selectedItem?.projectId}
        addButtonLabel={isExternalViewer ? undefined : tProjects.newProject}
        items={sidebarItems}
        toggleModal={isExternalViewer ? undefined : handleOpenCreateWizard}
        handleMenuItemClick={(item: any) => {
          onRememberedSelect(item.id);
        }}
        filterSlot={
          <div className="flex flex-col gap-2 w-full">
            {/* Walkthrough fix (sprint 38): in-progress AI plans listed openly, above the filter. */}
            {!isExternalViewer && <InProgressAiPlans variant="expanded" />}
            <ReactSelect
              value={selectedState}
              options={stateOptions}
              placeholder={tProjects.allStates}
              onChange={handleStateChange}
              isClearable={false}
              className="w-full"
            />
          </div>
        }
        disabled={readOnly}
      />

      {projectsLoading && (
        <div className="flex justify-center items-center h-full">
          <Loading size="xl" />
        </div>
      )}

      {!projectsLoading && projects.length === 0 && (
        <div className="flex flex-1 w-full h-full">
          <EmptyData
            fullHeight
            textButton={tProjects.newProject}
            onClickButton={handleOpenCreateWizard}
            description={
              selectedState
                ? `No ${selectedState.label} Projects Found`
                : tProjects.noProjectsYet
            }
            iconButton={<AddIcon sx={{ fontSize: '16px' }} />}
            disabled={readOnly}
          />
        </div>
      )}

      {!projectsLoading && project && (
        // Cap the row to the viewport minus the content wrapper's padding (py-6 =
        // 3rem) so nothing scrolls globally. ProjectCard, the right column's tab
        // content, and the feed each scroll internally instead.
        <div
          data-testid="projects-page-detail"
          className="flex gap-4 w-full h-[calc(100vh-3rem)] min-h-0">
          <ProjectCard
            project={project}
            readOnly={readOnly}
            isReviewer={isExternalViewer}
            goToClient={goToClient}
            onEdit={handleEditProject}
            goToSubcontractor={goToSubcontractor}
            onDelete={() => setIsDeleteProjectModalOpen(true)}
            onToggleFavorite={() => toggleFavorite(project.projectId as string)}
            onShowGeofence={(geofenceId) => {
              setSelectedGeofenceId(geofenceId ?? null);
              setIsGeofenceModalOpen(true);
            }}
            headerAction={
              <AssistantProjectButton projectId={project.projectId} />
            }
            onReportIncident={
              roleRank(role) >= ROLE_RANK.SUPERINTENDENT
                ? () => setIsReportIncidentModalOpen(true)
                : undefined
            }
          />
          <div className="flex flex-col gap-4 w-full h-full min-h-0 min-w-0">
            {/* Budget and Timeline cards are hidden from reviewers only (pre-story-107
                behavior). story-107 only restricts the Timeline card's Import Tasks
                action, a template action, to SUPERINTENDENT and above — everything
                else in this block stays visible to whoever could see it before. */}
            {!isExternalViewer && (
              <div className="grid grid-cols-1 gap-4 xl:gap-0 xl:grid-cols-2 w-full flex-shrink-0">
                <BudgetCard
                  budget={project.budget || 0}
                  totalCost={budgetSummary?.totalAmount ?? totalCost}
                  setIsBudgetModalOpen={() => setIsBudgetModalOpen(true)}
                  setBillOfMaterialsModalOpen={() =>
                    setIsBillOfMaterialsModalOpen(true)
                  }
                />
                <TimelineCard
                  endDate={project?.endDate as Date}
                  startDate={project?.startDate as Date}
                  totalManHours={project?.totalManHours}
                  onImportTasks={
                    roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK
                      ? () => setIsImportTemplateModalOpen(true)
                      : undefined
                  }
                />
              </div>
            )}
            <div data-testid="projects-page-tabs">
            <Tab
              className="mt-4 flex-shrink-0"
              tabs={[
                {
                  id: 'projectFeed',
                  name: tProjects.tabProjectFeed,
                  count: countFeed,
                  leftIcon: <WorkOutlineOutlinedIcon />,
                },
                {
                  id: 'documents',
                  name: tProjects.tabDocuments,
                  count: documentsData ? documentsData.pagination.total : 0,
                  leftIcon: <SourceOutlinedIcon />,
                },
                {
                  id: 'evidence',
                  name: tProjects.tabEvidence,
                  leftIcon: <PhotoCameraOutlinedIcon />,
                },
                // Visitors tab is hidden from external viewers (REVIEWER, CLIENT)
                ...(isExternalViewer
                  ? []
                  : [
                      {
                        id: 'visitors',
                        name: tProjects.tabVisitors,
                        count: visitorsNowCount,
                        countLabel: tProjects.visitorsNowLabel,
                        leftIcon: <GroupOutlinedIcon />,
                      },
                    ]),
                // Invoices tab (feature-gated; reviewers have no INVOICES access)
                ...(canAccess('INVOICES', role)
                  ? [
                      {
                        id: 'invoices',
                        name: tProjects.tabInvoices,
                        count: projectInvoices.length,
                        leftIcon: <ReceiptLongOutlinedIcon />,
                      },
                    ]
                  : []),
              ]}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
              }}
            />
            </div>

            {activeTab === 'projectFeed' && (
              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <ProjectFeed
                  activities={activities}
                  paginationActions={paginationActions}
                  totalCount={totalCount}
                  setCountFeed={setCountFeed}
                  handleOpenTaskDetail={handleOpenTaskDetail}
                  readOnly={readOnly}
                />
              </div>
            )}
            {(canAccess('DOCUMENTS', role) || isReviewer) && /* field-capture read exception: REVIEWER only, per user.role.enum.ts */
              activeTab === 'documents' && (
                <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                  <DocumentsTable
                    canEdit={!readOnly}
                    projectId={id}
                    showActions={!readOnly}
                    isReadOnly={readOnly}
                  />
                </div>
              )}
            {(canAccess('DOCUMENTS', role) || isReviewer) && /* field-capture read exception: REVIEWER only, per user.role.enum.ts */ activeTab === 'evidence' && (
              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <ProjectEvidenceTab projectId={id} />
              </div>
            )}
            {!isExternalViewer && activeTab === 'visitors' && (
              <div className="mt-4 flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <UsersInsideAreaList
                  filter={id ? { projectId: id } : null}
                  enabled={activeTab === 'visitors'}
                  resetKey={id}
                />
              </div>
            )}
            {canAccess('INVOICES', role) && activeTab === 'invoices' && (
              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <ProjectInvoicesTab
                  projectId={id}
                  projectName={project.name}
                  defaultRecipientEmail={project.client?.email}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isEdit && project && (
        <CreateEditProjectModal
          isOpen={isCreateEditProjectModalOpen}
          onClose={toggleCreateEditProjectModal}
          initialData={project}
          onSuccess={handleProjectSuccess}
        />
      )}

      <ConfirmationModal
        title={tProjects.deleteProjectTitle}
        onConfirm={deleteProject}
        confirmText={tCommon.delete}
        isOpen={isDeleteProjectModalOpen}
        onClose={() => setIsDeleteProjectModalOpen(false)}
        description={tProjects.confirmDeleteProject}
      />

      <ProjectGeofenceModal
        isOpen={isGeofenceModalOpen}
        onClose={() => {
          setIsGeofenceModalOpen(false);
          setSelectedGeofenceId(null);
        }}
        project={project ?? null}
        hideActiveToggle
        hideGeofencesLink={isExternalViewer}
        initialGeofenceId={selectedGeofenceId}
      />

      <BudgetTableModal
        projectId={id}
        selectedItem={project}
        isOpen={isBudgetModalOpen}
        isDelegated={!!project?.originalProjectId}
        onClose={() => setIsBudgetModalOpen(false)}
      />

      <BillOfMaterialsModal
        inventory={inventory}
        projectName={project?.name || tProjects.projectFallbackName}
        isOpen={isBillOfMaterialsModalOpen}
        onClose={() => setIsBillOfMaterialsModalOpen(false)}
      />

      <TaskDetailModal
        task={task}
        project={project as Project}
        isTaskDetailModal={isTaskDetailModal}
        setIsTaskDetailModal={setIsTaskDetailModal}
      />

      <ImportTaskTemplateModal
        isOpen={isImportTemplateModalOpen}
        onClose={() => setIsImportTemplateModalOpen(false)}
        projectId={id || ''}
        projectStartDate={project?.startDate || undefined}
        projectEndDate={project?.endDate || undefined}
        onSuccess={handleImportTemplateSuccess}
      />

      {project && roleRank(role) >= ROLE_RANK.SUPERINTENDENT && (
        <ReportIncidentModal
          open={isReportIncidentModalOpen}
          onClose={() => setIsReportIncidentModalOpen(false)}
          projectId={project.projectId}
          projectName={project.name}
        />
      )}
    </>
  );
};

export default ProjectsPage;
